import React, { memo, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';

import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSpaceType } from '@confluence/create-space/entry-points/useSpaceType.ts';

import type { ContentStateButtonProps } from '../titleareaContentComponentsTypes.ts';

export const ContentStateHoverButton: React.MemoExoticComponent<
	({
		contentId,
		hasContentState,
		contentType,
		userOffline,
		setIsContentStateButtonFocused,
		setIsContentStateMenuOpen,
		floatingToolbarButtonStyle,
	}: ContentStateButtonProps) => React.JSX.Element | null
> = memo(
	({
		contentId,
		hasContentState,
		contentType,
		userOffline,
		setIsContentStateButtonFocused,
		setIsContentStateMenuOpen,
		floatingToolbarButtonStyle,
	}: ContentStateButtonProps): React.JSX.Element | null => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const [spaceKey] = usePageSpaceKey();
		const { isKnowledgeBaseSpace } = useSpaceType({ spaceKey });

		const handleClick = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateButtonClicked',
					source: 'editorContentStateButton',
				},
			}).fire();
		}, [contentId, createAnalyticsEvent]);

		const handleMouseEnter = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'hovered',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateButtonHovered',
					source: 'editorContentStateButton',
				},
			}).fire();
		}, [contentId, createAnalyticsEvent]);

		const handleFocus = useCallback(() => {
			setIsContentStateButtonFocused(true);
		}, [setIsContentStateButtonFocused]);

		const handleBlur = useCallback(() => {
			setIsContentStateButtonFocused(false);
		}, [setIsContentStateButtonFocused]);

		// only want to show hover button if we don't have a content state and if not in a knowledge base space
		// otherwise it will be next to the full-width button
		return !hasContentState && !isKnowledgeBaseSpace ? (
			// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
			<Box
				testId="content-state-button-wrapper"
				onClick={handleClick}
				onMouseEnter={handleMouseEnter}
				onFocus={handleFocus}
				onBlur={handleBlur}
			>
				<EditorContentState
					contentId={contentId}
					contentType={contentType}
					userOffline={userOffline}
					onContentStateMenuToggle={setIsContentStateMenuOpen}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					isViewMode={false}
				/>
			</Box>
		) : null;
	},
);
